/**
 * @typedef {Object} MailingAddress
 * @property {string} addressLine1
 * @property {string} addressLine2
 * @property {string} city
 * @property {string} region
 * @property {string} postalCode
 * @property {'US'|'CA'} countryCode
 */

export const MAILING_ADDRESS_COUNTRIES = [
  { key: 'US', label: 'United States' },
  { key: 'CA', label: 'Canada' },
];

export const US_STATES = [
  { key: 'AL', label: 'Alabama' },
  { key: 'AK', label: 'Alaska' },
  { key: 'AZ', label: 'Arizona' },
  { key: 'AR', label: 'Arkansas' },
  { key: 'CA', label: 'California' },
  { key: 'CO', label: 'Colorado' },
  { key: 'CT', label: 'Connecticut' },
  { key: 'DE', label: 'Delaware' },
  { key: 'FL', label: 'Florida' },
  { key: 'GA', label: 'Georgia' },
  { key: 'HI', label: 'Hawaii' },
  { key: 'ID', label: 'Idaho' },
  { key: 'IL', label: 'Illinois' },
  { key: 'IN', label: 'Indiana' },
  { key: 'IA', label: 'Iowa' },
  { key: 'KS', label: 'Kansas' },
  { key: 'KY', label: 'Kentucky' },
  { key: 'LA', label: 'Louisiana' },
  { key: 'ME', label: 'Maine' },
  { key: 'MD', label: 'Maryland' },
  { key: 'MA', label: 'Massachusetts' },
  { key: 'MI', label: 'Michigan' },
  { key: 'MN', label: 'Minnesota' },
  { key: 'MS', label: 'Mississippi' },
  { key: 'MO', label: 'Missouri' },
  { key: 'MT', label: 'Montana' },
  { key: 'NE', label: 'Nebraska' },
  { key: 'NV', label: 'Nevada' },
  { key: 'NH', label: 'New Hampshire' },
  { key: 'NJ', label: 'New Jersey' },
  { key: 'NM', label: 'New Mexico' },
  { key: 'NY', label: 'New York' },
  { key: 'NC', label: 'North Carolina' },
  { key: 'ND', label: 'North Dakota' },
  { key: 'OH', label: 'Ohio' },
  { key: 'OK', label: 'Oklahoma' },
  { key: 'OR', label: 'Oregon' },
  { key: 'PA', label: 'Pennsylvania' },
  { key: 'RI', label: 'Rhode Island' },
  { key: 'SC', label: 'South Carolina' },
  { key: 'SD', label: 'South Dakota' },
  { key: 'TN', label: 'Tennessee' },
  { key: 'TX', label: 'Texas' },
  { key: 'UT', label: 'Utah' },
  { key: 'VT', label: 'Vermont' },
  { key: 'VA', label: 'Virginia' },
  { key: 'WA', label: 'Washington' },
  { key: 'WV', label: 'West Virginia' },
  { key: 'WI', label: 'Wisconsin' },
  { key: 'WY', label: 'Wyoming' },
  { key: 'DC', label: 'District of Columbia' },
];

export const CANADIAN_PROVINCES_AND_TERRITORIES = [
  { key: 'AB', label: 'Alberta' },
  { key: 'BC', label: 'British Columbia' },
  { key: 'MB', label: 'Manitoba' },
  { key: 'NB', label: 'New Brunswick' },
  { key: 'NL', label: 'Newfoundland and Labrador' },
  { key: 'NS', label: 'Nova Scotia' },
  { key: 'NT', label: 'Northwest Territories' },
  { key: 'NU', label: 'Nunavut' },
  { key: 'ON', label: 'Ontario' },
  { key: 'PE', label: 'Prince Edward Island' },
  { key: 'QC', label: 'Quebec' },
  { key: 'SK', label: 'Saskatchewan' },
  { key: 'YT', label: 'Yukon' },
];

const COUNTRY_NAMES = MAILING_ADDRESS_COUNTRIES.reduce(
  (names, country) => ({ ...names, [country.key]: country.label }),
  {}
);

const US_ZIP_REGEXP = /^\d{5}(?:-\d{4})?$/;
const CANADIAN_POSTAL_CODE_REGEXP = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTVWXYZ]\d[ABCEGHJ-NPRSTVWXYZ]\d$/i;

export const createEmptyMailingAddress = () => ({
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postalCode: '',
  countryCode: 'US',
});

/**
 * Builds stable form values for a stored address. Unsupported or missing
 * countries fall back to the default country used by the address form.
 *
 * @param {Partial<MailingAddress>} address
 * @returns {MailingAddress}
 */
export const getMailingAddressFormValues = address => {
  const source = address || {};
  const countryCode =
    typeof source.countryCode === 'string' ? source.countryCode.trim().toUpperCase() : '';
  const supportedCountryCode = MAILING_ADDRESS_COUNTRIES.some(
    country => country.key === countryCode
  )
    ? countryCode
    : 'US';

  return {
    ...createEmptyMailingAddress(),
    ...source,
    countryCode: supportedCountryCode,
  };
};

export const getMailingAddressRegionOptions = countryCode =>
  countryCode === 'CA' ? CANADIAN_PROVINCES_AND_TERRITORIES : US_STATES;

export const isMailingAddressRegionValid = (region, countryCode) =>
  getMailingAddressRegionOptions(countryCode).some(option => option.key === region);

export const normalizeCanadianPostalCode = value => {
  const compactValue = typeof value === 'string' ? value.replace(/\s/g, '').toUpperCase() : '';
  return compactValue.length === 6
    ? `${compactValue.slice(0, 3)} ${compactValue.slice(3)}`
    : compactValue;
};

export const isMailingAddressPostalCodeValid = (postalCode, countryCode) => {
  const value = typeof postalCode === 'string' ? postalCode.trim() : '';
  if (countryCode === 'US') return US_ZIP_REGEXP.test(value);
  if (countryCode === 'CA') {
    return CANADIAN_POSTAL_CODE_REGEXP.test(value.replace(/\s/g, ''));
  }
  return false;
};

/**
 * Trims form input and canonicalizes country, region, and Canadian postal codes
 * before they are written to private profile data.
 *
 * @param {Partial<MailingAddress>} address
 * @returns {MailingAddress}
 */
export const normalizeMailingAddress = address => {
  const source = address || {};
  const countryCode =
    typeof source.countryCode === 'string' ? source.countryCode.trim().toUpperCase() : '';
  const postalCode = typeof source.postalCode === 'string' ? source.postalCode.trim() : '';

  return {
    addressLine1: typeof source.addressLine1 === 'string' ? source.addressLine1.trim() : '',
    addressLine2: typeof source.addressLine2 === 'string' ? source.addressLine2.trim() : '',
    city: typeof source.city === 'string' ? source.city.trim() : '',
    region: typeof source.region === 'string' ? source.region.trim().toUpperCase() : '',
    postalCode: countryCode === 'CA' ? normalizeCanadianPostalCode(postalCode) : postalCode,
    countryCode,
  };
};

export const isMailingAddressComplete = address => {
  const normalized = normalizeMailingAddress(address);
  return (
    !!normalized.addressLine1 &&
    !!normalized.city &&
    MAILING_ADDRESS_COUNTRIES.some(country => country.key === normalized.countryCode) &&
    isMailingAddressRegionValid(normalized.region, normalized.countryCode) &&
    isMailingAddressPostalCodeValid(normalized.postalCode, normalized.countryCode)
  );
};

export const formatMailingAddressLines = address => {
  if (!isMailingAddressComplete(address)) return [];

  const normalized = normalizeMailingAddress(address);
  return [
    normalized.addressLine1,
    normalized.addressLine2,
    `${normalized.city}, ${normalized.region} ${normalized.postalCode}`,
    COUNTRY_NAMES[normalized.countryCode],
  ].filter(Boolean);
};
